import React, { memo, useState, useEffect, forwardRef } from 'react';
import Highcharts from 'highcharts/highstock';
import HighchartsReact from 'highcharts-react-official';
import moment from 'moment';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { fixZero } from '@utils/widget';
import D_Currency from '@utils/currency';
import {
  defaultAxis,
  chartStyle,
  xAxisDateFormatStock,
  xAxisStyleDefault,
  yAxisStyleDefault,
  defaultToolTip,
} from '../config';
import useTheme from '@hooks/useTheme';

import { produce } from 'immer';

// import '@style/components/charts.scss';
import '../../../styles/components/charts.scss';
const selectedTemp = {
  current: {},
};

const currencyIsoCodeTemp = {
  current: {},
};

const companyNameTemp = {
  current: '',
};

const BUTTONTYPE = {
  YEAR: 'year',
  MONTH: 'month',
};

const SEASONS = [10, 7, 4, 1];
const SEASONSLABEL = ['Oct', 'Jul', 'Apr', 'Jan'];

const formatCurrencyToString = (value, currencyIsoCode) => {
  if (typeof value === 'undefined' || typeof currencyIsoCode === 'undefined') {
    return '-';
  }
  return D_Currency.format({ value, currencyIsoCode });
};

const formatyaxis = function (e) {
  const currency =
    currencyIsoCodeTemp.current[companyNameTemp.current] || 'USD';
  return `${formatCurrencyToString(this.value, currency)}`;
};

const formatyvalue = function (name) {
  const currency = currencyIsoCodeTemp.current[name] || 'USD';

  const date = new Date(this.x),
    year = date.getFullYear(),
    month = date.getMonth() + 1,
    day = date.getDate();

  return (
    `<b>${moment(`${fixZero(month)}/${fixZero(day)}/${year}`).format(
      'MMM DD, YYYY',
    )}</b>` +
    '<br/>' +
    `<b>${name}</b>: ` +
    `<span>${formatCurrencyToString(this.y, currency)}</span>`
  );
};

const tickPositioner = function (e, selector, name) {
  const _selector = selectedTemp.current[name];
  if (Number(_selector) > -1 && _selector !== selector) {
    selector = _selector;
  }

  try {
    const startDate = new Date(this.dataMin),
      endDate = new Date(this.dataMax),
      startYear = startDate.getFullYear(),
      endYear = endDate.getFullYear(),
      endMonth = endDate.getMonth() + 1,
      { type, count } = this.chart.rangeSelector.buttonOptions[selector];
    if (type === BUTTONTYPE.YEAR && count === 1) {
      let currentMonth = endMonth;

      while (!SEASONS.includes(currentMonth)) {
        currentMonth--;
      }

      const seasonIndex = SEASONS.indexOf(currentMonth);

      return [...SEASONS]
        .splice(seasonIndex)
        .concat(SEASONS)
        .slice(0, SEASONS.length)
        .map((month) => {
          const year = month > endMonth ? endYear - 1 : endYear;
          const date = `${year}-${month}-1`;

          return new Date(date).getTime();
        })
        .filter((t) => t >= this.dataMin);
    } else if (type === BUTTONTYPE.YEAR) {
      let years = [startYear],
        startYearTemp = startYear;
      while (startYearTemp < endYear) {
        startYearTemp++;
        years.push(startYearTemp);
      }

      if (count > 1) {
        const yearsTemp = [...years].reverse();

        years = yearsTemp.slice(0, count).reverse();
      }

      years = years
        .map((year) => new Date(String(year)).getTime())
        .filter((t) => t >= this.dataMin);

      if (years.length === 0) {
        years = [this.dataMin];
      }

      return years;
    }
    return null;
  } catch (error) {
    return null;
  }
};

const xAxisFormatter = function (selector, name) {
  const _selector = selectedTemp.current[name];
  if (_selector && _selector !== selector) {
    selector = _selector;
  }

  try {
    const { type, count } = this.chart.rangeSelector.buttonOptions[selector],
      date = new Date(this.value),
      year = date.getFullYear() % 100,
      month = date.getMonth() + 1,
      label = `FY${year}`;

    if (type === BUTTONTYPE.YEAR && count === 1) {
      return `${SEASONSLABEL[SEASONS.indexOf(month)]}'${year}`;
    }

    if (type === BUTTONTYPE.MONTH) {
      return `${Highcharts.dateFormat('%b %e', this.value)},${year}`;
    }

    return label;
  } catch (error) {
    return `FY${Number(Highcharts.dateFormat('%Y', this.value)) % 100}`;
  }
};

const StockChart = forwardRef((props, ref) => {
  const { data, selector, name, timeRangeBtns, currencyIsoCode } = props;
  const { theme } = useTheme();
  const isLightMode = theme === 'light';

  const defaultOptions = {
    chart: {
      panning: {
        enabled: false,
      },
    },
    credits: {
      enabled: false,
    },
    scrollbar: {
      enabled: false,
    },
    navigator: {
      enabled: false,
    },
    rangeSelector: {
      buttonSpacing: 40,
      labelStyle: {
        display: 'none',
      },
      inputEnabled: false,
      buttons: timeRangeBtns,
      selected: selector,
    },
    tooltip: {
      ...defaultToolTip,
      formatter: formatyvalue,
    },
    xAxis: {
      ...defaultAxis,
      breaks: [true],
      type: 'datetime',
      labels: {
        style: xAxisStyleDefault,
      },
      dateTimeLabelFormats: xAxisDateFormatStock,
    },
    yAxis: {
      ...defaultAxis,
      labels: {
        formatter: formatyaxis,
        style: yAxisStyleDefault,
      },
    },
    series: [
      {
        name,
        // color: '#FFB600',
        lineWidth: 1,
        data: [],
        tooltip: {
          valueDecimals: 2,
        },
      },
    ],
  };
  if (isLightMode) {
    delete defaultOptions.series[0].color;
  } else {
    defaultOptions.series[0].color = '#FFB600';
  }

  const [options, setOptions] = useState(defaultOptions);

  useEffect(() => {
    selectedTemp.current[name] = selector;
    currencyIsoCodeTemp.current[name] = currencyIsoCode;
    companyNameTemp.current = name;
    if (data) {
      const newOptions = produce(options, (draft) => {
        draft.series[0].data = data;
        draft.rangeSelector.selected = selector;

        draft.xAxis = {
          ...options.xAxis,
          tickPositioner(e) {
            return tickPositioner.call(this, e, selector, name);
          },
        };

        draft.xAxis.labels = {
          ...options.xAxis.labels,
          formatter() {
            return xAxisFormatter.call(this, selector, name);
          },
        };

        draft.tooltip.formatter = function () {
          return formatyvalue.call(this, name);
        };
      });

      if (
        options.series[0].data.length !== newOptions.series[0].data.length ||
        options.rangeSelector.selected !== newOptions.rangeSelector.selected
      ) {
        setOptions(newOptions);
      }
    }
  }, [data, selector, name, options, currencyIsoCode]);

  useEffect(() => {
    if (ref.current && ref.current.chart) {
      ref.current.chart.update(options);
    }
  }, [options, ref]);

  useEffect(
    () => () => {
      selectedTemp.current = {};
      currencyIsoCodeTemp.current = {};
      companyNameTemp.current = '';
    },
    [],
  );

  return (
    <div
      className={classnames('stock-highchart-wrapper', {
        'highcharts-dark': !isLightMode,
        'highcharts-light': isLightMode,
      })}
    >
      <HighchartsReact
        ref={ref}
        highcharts={Highcharts}
        constructorType="stockChart"
        options={options}
        containerProps={{
          style: chartStyle,
        }}
      />
    </div>
  );
});

StockChart.defaultProps = {
  data: [],
};

StockChart.propTypes = {
  data: PropTypes.array,
  selector: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  currencyIsoCode: PropTypes.string.isRequired,
};

export default memo(StockChart);
